// The minimap (top right; N hides it) and the world map (M): the overworld with every place on it, then how
// the town's districts fit together, where you are, and where your party and friends are.
import { ZONES, zoneDef, PLACES, placeOf, worldOf } from '../../../shared/zones.js';
import { OTHER_HOMELANDS } from '../../../shared/homelands.js';
import { ROUTES, dueText, vesselsAt } from '../../../shared/routes.js';
import { miniVessel } from '../render/vessels.js';
import { INTERIORS } from '../../../shared/interiors.js';
import type { Scene } from '../render/scene.js';

const $ = <T extends HTMLElement = HTMLElement>(id: string) => document.getElementById(id) as T;
export interface Dot { x: number; y: number; kind: 'me' | 'party' | 'player' | 'npc' | 'mob' | 'boss'; dir?: number }
export interface Mark { x: number; y: number; kind: 'exit' | 'door' | 'shrine' | 'battle' | 'find'; label: string; open?: boolean; r?: number }

/** Where a zone name (as the server reports it) is on the world map. */
export function zoneOfName(name: string): string | null {
  const z = ZONES.find(z => z.name === name); if (z) return z.id;
  const room = INTERIORS.find(r => r.name === name && r.id !== 'home');
  if (room) return ({ smithy: 'artisan', carpenter: 'artisan', weaver: 'artisan', harbor_office: 'harbor', fish_market: 'harbor', warehouse: 'harbor' } as Record<string, string>)[room.id] ?? 'town';
  if (/Cottage$/.test(name)) return 'cottages';
  return null;
}

const BLURB: Record<string, string> = {
  town: 'The fountain, the bakery, the guild hall.', harbor: 'Boats, piers and the fish market.', artisan: 'The smithy, the carpenter and the weaver.',
  cottages: 'Every door here leads to your own home.', gate: 'The way out of town, onto the world map.',
};
const TOWNISH = new Set(['town', 'harbor', 'artisan', 'cottages', 'gate']);

export class Maps {
  private mini = $<HTMLCanvasElement>('minimap'); private mctx = this.mini.getContext('2d')!;
  private box = $('minimapBox');
  private world = $('worldmap');
  shown = true;
  onOpen: (open: boolean) => void = () => {};
  /** Filled in by the game: scenes by zone id (painted on first visit), and where people are. */
  scene: (id: string) => Scene | null = () => null;
  people: () => { party: Map<string, string[]>; friends: Map<string, string[]> } = () => ({ party: new Map(), friends: new Map() });

  constructor() {
    try { this.shown = localStorage.getItem('tt-minimap') !== 'off'; } catch { /* private mode */ }
    this.box.hidden = true;
    this.box.addEventListener('click', () => this.toggleWorld(true));
    $('worldClose').addEventListener('click', () => this.toggleWorld(false));
    this.world.addEventListener('click', e => { if (e.target === this.world) this.toggleWorld(false); });
    addEventListener('keydown', e => {
      if (/INPUT|TEXTAREA|SELECT/.test((document.activeElement as HTMLElement)?.tagName || '') || this.box.dataset.off === '1') return;
      if (e.code === 'KeyM' && !e.repeat) { e.preventDefault(); this.toggleWorld(); }
      else if (e.code === 'KeyN' && !e.repeat) { e.preventDefault(); this.shown = !this.shown; try { localStorage.setItem('tt-minimap', this.shown ? 'on' : 'off'); } catch { /* fine */ } this.box.classList.toggle('off', !this.shown); }
      else if (e.code === 'Escape' && !this.world.hidden) { e.preventDefault(); this.toggleWorld(false); }
    });
    this.box.classList.toggle('off', !this.shown);
  }

  /** In the world (or not): the minimap shows only while playing. */
  setPlaying(on: boolean): void { this.box.hidden = !on; this.box.dataset.off = on ? '' : '1'; if (!on) this.toggleWorld(false); }

  private zone = ''; private name = '';
  setZone(id: string, name: string, layer: number): void {
    this.zone = id; this.name = name;
    $('minimapName').textContent = `${name}${layer > 1 ? ` · ${layer}` : ''}`;
    if (!this.world.hidden) this.renderWorld();
  }

  /** Redraw the minimap: the zone's picture around you, marks, and dots. Cheap: call a few times a second. */
  draw(scene: Pick<Scene, 'w' | 'h' | 'thumb'> | null, px: number, py: number, dots: Dot[], marks: Mark[], now: number): void {
    if (!this.shown || this.box.hidden) return;
    const c = this.mctx, W = this.mini.width, H = this.mini.height;
    // pixels per tile: 3 out of doors; a room is small, so it fills the box
    const Z = scene && scene.w * 3 < W && scene.h * 3 < H ? Math.min(W / scene.w, H / scene.h) * 0.94 : 3;
    c.imageSmoothingEnabled = true;
    c.fillStyle = '#e9dfca'; c.fillRect(0, 0, W, H);
    if (!scene) { c.fillStyle = '#efe4c8'; c.font = '700 10px system-ui'; c.textAlign = 'center'; c.fillText('Indoors', W / 2, H / 2 + 3); return; }
    const tx = px / 16, ty = py / 16;
    let ox = W / 2 - tx * Z, oy = H / 2 - ty * Z;
    // keep the map edge in view rather than showing empty space, when the zone is bigger than the window
    if (scene.w * Z > W) ox = Math.min(0, Math.max(W - scene.w * Z, ox)); else ox = (W - scene.w * Z) / 2;
    if (scene.h * Z > H) oy = Math.min(0, Math.max(H - scene.h * Z, oy)); else oy = (H - scene.h * Z) / 2;
    c.drawImage(scene.thumb(), Math.round(ox), Math.round(oy), scene.w * Z, scene.h * Z);
    const P = (x: number, y: number): [number, number] => [Math.round(ox + x / 16 * Z), Math.round(oy + y / 16 * Z)];
    for (const m of marks) {
      const [x, y] = P(m.x, m.y);
      if (m.kind === 'battle') { c.strokeStyle = m.open ? '#8fe08a' : '#ec5448'; c.lineWidth = 1; c.beginPath(); c.arc(x, y, Math.max(3, (m.r ?? 40) / 16 * Z), 0, Math.PI * 2); c.stroke(); c.fillStyle = m.open ? '#8fe08a' : '#ec5448'; c.fillRect(x - 1, y - 1, 2, 2); }
      else if (m.kind === 'find') {   // the person you're looking for: a pulsing ring (pinned to the edge, with an arrow, when off the map)
        const cx = Math.max(6, Math.min(W - 6, x)), cy = Math.max(6, Math.min(H - 6, y)), off = cx !== x || cy !== y, r = 4 + (now / 160 % 4);
        c.strokeStyle = '#d64bb4'; c.lineWidth = 1.5; c.beginPath(); c.arc(cx, cy, off ? 4 : r, 0, Math.PI * 2); c.stroke(); c.fillStyle = '#d64bb4'; c.fillRect(cx - 1.5, cy - 1.5, 3, 3);
        if (off) { const a = Math.atan2(y - cy, x - cx); c.beginPath(); c.moveTo(cx + Math.cos(a) * 7, cy + Math.sin(a) * 7); c.lineTo(cx + Math.cos(a + 2.5) * 4, cy + Math.sin(a + 2.5) * 4); c.lineTo(cx + Math.cos(a - 2.5) * 4, cy + Math.sin(a - 2.5) * 4); c.fill(); }
      }
      else if (m.kind === 'shrine') { c.fillStyle = '#2f2a26'; c.fillRect(x - 3, y - 3, 7, 7); c.fillStyle = '#a3def4'; c.fillRect(x - 2, y - 2, 5, 5); c.fillStyle = '#f2fbff'; c.fillRect(x - 1, y - 1, 2, 2); }
      else if (m.kind === 'exit') { const pulse = Math.floor(now / 400) % 2; c.fillStyle = '#2f2a26'; c.fillRect(x - 3, y - 3, 7, 7); c.fillStyle = pulse ? '#ffe9a8' : '#f9c776'; c.fillRect(x - 2, y - 2, 5, 5); }
      else { c.fillStyle = '#7a473f'; c.fillRect(x - 1, y - 1, 3, 3); }
    }
    if (this.vessels && this.zone.split('~')[0] === 'world') for (const v of vesselsAt(Date.now())) { const [x, y] = P(v.x * 16 + 8, v.y * 16 + 8); if (x > -4 && y > -4 && x < W + 4 && y < H + 4) miniVessel(c, v, x, y); }
    const order = { npc: 0, player: 1, mob: 2, boss: 3, party: 4, me: 5 };
    for (const d of dots.sort((a, b) => order[a.kind] - order[b.kind])) {
      const [x, y] = P(d.x, d.y);
      if (x < -3 || y < -3 || x > W + 3 || y > H + 3) continue;
      if (d.kind === 'me') {
        c.fillStyle = '#2f2a26'; c.fillRect(x - 3, y - 3, 7, 7); c.fillStyle = '#ffffff'; c.fillRect(x - 2, y - 2, 5, 5);
        const [fx, fy] = [[0, 1], [1, 0], [0, -1], [-1, 0]][d.dir ?? 0]; c.fillStyle = '#db614f'; c.fillRect(x + fx * 2 - (fx ? 0 : 1), y + fy * 2 - (fy ? 0 : 1), fx ? 1 : 3, fy ? 1 : 3);
      } else {
        const col = d.kind === 'party' ? '#8fe08a' : d.kind === 'player' ? '#f3eee3' : d.kind === 'npc' ? '#f9c776' : d.kind === 'boss' ? '#ec5448' : '#f29b6b';
        const s = d.kind === 'party' ? 4 : 3;
        c.fillStyle = '#2f2a26'; c.fillRect(x - 1 - (s > 3 ? 1 : 0), y - 1 - (s > 3 ? 1 : 0), s, s); c.fillStyle = col; c.fillRect(x - (s > 3 ? 1 : 0), y - (s > 3 ? 1 : 0), s - 1, s - 1);
      }
    }
  }

  toggleWorld(force?: boolean): void {
    const open = force ?? this.world.hidden;
    if (open === !this.world.hidden) return;
    this.world.hidden = !open;
    if (open) this.renderWorld();
    this.onOpen(open);
  }

  private renderWorld(): void {
    const grid = $('worldGrid'); grid.replaceChildren();
    const zid = this.zone.split('~')[0];
    const here = zid.split(':')[0] === 'home' ? 'cottages' : (zoneDef(zid) ? zid : zoneOfName(this.name) ?? 'town');
    const { party, friends } = this.people();
    // the wide world: the overworld picture, a pin on every place, you and your friends on it
    // which continent: you see the world map of the homeland you're in
    const W0 = worldOf(here), home = OTHER_HOMELANDS.find(n => n.zones.some(z => z.id === here || z.id === W0));
    const places = PLACES.filter(p => (p.world ?? 'world') === W0);
    const townOf = (z: string) => home ? (zoneDef(z)?.kind === 'town' ? places.find(p => p.zone && zoneDef(p.zone)?.kind === 'town')?.id : undefined) : undefined;
    const hereP = here === W0 ? null : TOWNISH.has(here) ? 'town' : townOf(here) ?? placeOf(here)?.id ?? null;
    const sc = this.scene(W0);
    const wrap = document.createElement('div'); wrap.className = 'wm-world';
    if (sc) { const t = sc.thumb(), img = document.createElement('canvas'); img.width = t.width; img.height = t.height; img.getContext('2d')!.drawImage(t, 0, 0); wrap.append(img); }
    for (const pl of places) {
      const pin = document.createElement('div'); pin.className = 'wm-pin' + (pl.locked ? ' locked' : '') + (pl.id === hereP ? ' here' : '');
      pin.style.left = `${(pl.tile[0] + 0.5) / (sc?.w ?? 80) * 100}%`; pin.style.top = `${(pl.tile[1] + 0.5) / (sc?.h ?? 52) * 100}%`;
      const who = [...[...party].filter(([z]) => (TOWNISH.has(z) ? 'town' : placeOf(z)?.id) === pl.id).flatMap(([, n]) => n.map(x => `🟢 ${x}`)),
        ...[...friends].filter(([z]) => (TOWNISH.has(z) ? 'town' : placeOf(z)?.id) === pl.id).flatMap(([, n]) => n.map(x => `★ ${x}`))];
      const b = document.createElement('b'); b.textContent = (pl.id === hereP ? '📍 ' : '') + pl.name + (who.length ? ` · ${who.length}` : '');
      const sm = document.createElement('small'); sm.textContent = pl.id === 'peak' ? this.bossLabel() : pl.levels; if (pl.id === 'peak' && this.boss?.open) pin.classList.add('awake');
      pin.append(b); if (pl.levels) pin.append(sm);
      pin.title = `${pl.name}${pl.levels ? ` (${pl.levels})` : ''}\n${pl.blurb}${pl.locked ? `\n${pl.locked}` : ''}${who.length ? '\n' + who.join('\n') : ''}`;
      wrap.append(pin);
    }
    if (here === W0 && this.me) {
      const dot = document.createElement('div'); dot.className = 'wm-me';
      dot.style.left = `${this.me[0] / 16 / (sc?.w ?? 80) * 100}%`; dot.style.top = `${this.me[1] / 16 / (sc?.h ?? 52) * 100}%`; wrap.append(dot);
    }
    const timetable = W0 === 'world' ? this.renderVessels(wrap, sc?.w ?? 80, sc?.h ?? 52) : null;
    const wcap = document.createElement('small'); wcap.className = 'wm-cap'; wcap.textContent = 'Walk onto a place to go in. Hover a place to read about it.' + (this.vessels && W0 === 'world' ? ' The ferry and the airship keep to a timetable: hover one to see when it’s due.' : '');
    grid.append(wrap, wcap); if (timetable) grid.append(timetable);
    // the town's districts
    const town = document.createElement('div'); town.className = 'wm-town';
    const tcap = document.createElement('small'); tcap.className = 'wm-cap'; tcap.textContent = home ? (home.zones.find(z => z.kind === 'town')?.name ?? '') : 'Tiny Town';
    for (const z of (home ? home.zones.filter(z => z.kind === 'town') : ['harbor', 'artisan', 'town', 'cottages', 'gate'].map(id => zoneDef(id)!))) {
      if (!home && !TOWNISH.has(z.id)) continue;
      const card = document.createElement('div'); card.className = 'wm-zone' + (z.id === here ? ' here' : '');
      const zs = this.scene(z.id);
      if (zs) { const img = document.createElement('canvas'); const t = zs.thumb(); img.width = t.width; img.height = t.height; img.getContext('2d')!.drawImage(t, 0, 0); card.append(img); }
      const name = document.createElement('b'); name.textContent = z.name; card.append(name);
      const sub = document.createElement('small'); sub.textContent = BLURB[z.id] ?? ''; card.append(sub);
      const who = [...(party.get(z.id) ?? []).map(n => `🟢 ${n}`), ...(friends.get(z.id) ?? []).map(n => `★ ${n}`)];
      if (z.id === here) who.unshift('📍 You are here');
      if (who.length) { const w = document.createElement('div'); w.className = 'wm-who'; w.textContent = who.slice(0, 4).join('\n') + (who.length > 4 ? `\n+${who.length - 4} more` : ''); card.append(w); }
      card.title = `${z.name}: ${BLURB[z.id] ?? ''} (Town: a safe place, no fighting)`;
      town.append(card);
    }
    grid.append(tcap, town);
    // the wide sea: the three homelands, far apart, and the ferries between their ports
    const sea = document.createElement('div'); sea.className = 'wm-sea';
    const scap = document.createElement('small'); scap.className = 'wm-cap'; scap.textContent = 'The three homelands lie far apart across open ocean. Ferries sail between their ports (a pass and a fare; a long crossing).';
    const lands: [string, string, string][] = [['world', 'Sunvale', '#d9564a'], ...OTHER_HOMELANDS.map(n => [n.zones.find(z => z.kind === 'world')?.id ?? '', n.id === 'thornwood' ? 'Thornwood' : 'Moonmere', n.id === 'thornwood' ? '#8fbf5a' : '#4e83ad'] as [string, string, string])];
    lands.forEach(([wid, name, col], i) => {
      if (i) { const route = document.createElement('div'); route.className = 'wm-route'; route.textContent = '⛴ · · · · ·'; sea.append(route); }
      const card = document.createElement('div'); card.className = 'wm-land' + (wid === W0 ? ' here' : ''); card.style.borderColor = col;
      const zs = wid ? this.scene(wid) : null;
      if (zs) { const img = document.createElement('canvas'), t = zs.thumb(); img.width = t.width; img.height = t.height; img.getContext('2d')!.drawImage(t, 0, 0); card.append(img); }
      const b = document.createElement('b'); b.textContent = (wid === W0 ? '📍 ' : '') + name; b.style.color = col; card.append(b);
      sea.append(card);
    });
    grid.append(scap, sea);
  }
  /** The world boss: awake now, and when that changes (0: held by a developer). */
  boss: { open: boolean; at: number } | null = null;
  bossLabel(): string {
    const b = this.boss; if (!b) return 'World boss';
    if (!b.at) return b.open ? 'Awake!' : 'Asleep';
    const mins = Math.max(0, Math.round((b.at - Date.now()) / 60000)), t = mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins}m`;
    return b.open ? `Awake! (${t} left)` : `Wakes in ${t}`;
  }
  /** Where you are, in pixels, for the you-are-here dot on the overworld. */
  me: [number, number] | null = null;
  /** The ferry and the airship (a setting). */
  vessels = true;
  private vesselTimer = 0;
  /** On the world map: their routes, the vessels themselves, and a timetable (hover for when each is next due). */
  private renderVessels(wrap: HTMLElement, W: number, H: number): HTMLElement | null {
    if (!this.vessels) return null;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg'); svg.setAttribute('class', 'wm-routes'); svg.setAttribute('viewBox', `0 0 ${W} ${H}`); svg.setAttribute('preserveAspectRatio', 'none');
    for (const r of ROUTES) {
      const pl = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
      pl.setAttribute('points', r.path.map(([x, y]) => `${x + 0.5},${y + 0.5}`).join(' ')); pl.setAttribute('class', r.kind);
      svg.append(pl);
      for (const s of r.stops) { const c = document.createElementNS('http://www.w3.org/2000/svg', 'circle'); const [x, y] = r.path[s.i]; c.setAttribute('cx', String(x + 0.5)); c.setAttribute('cy', String(y + 0.5)); c.setAttribute('r', '0.7'); c.setAttribute('class', r.kind); svg.append(c); }
    }
    wrap.append(svg);
    const pins = new Map<string, HTMLElement>();
    for (const r of ROUTES) { const d = document.createElement('div'); d.className = `wm-vessel ${r.kind}`; d.textContent = r.kind === 'ship' ? '⛵' : '🎈'; wrap.append(d); pins.set(r.id, d); }
    const table = document.createElement('div'); table.className = 'wm-timetable';
    const update = () => {
      const vs = vesselsAt(Date.now());
      for (const v of vs) {
        const d = pins.get(v.id)!; d.style.left = `${(v.x + 0.5) / W * 100}%`; d.style.top = `${(v.y + 0.5) / H * 100}%`;
        d.title = `${v.name}${v.docked ? ` (at ${v.docked})` : ` (heading for ${v.heading})`}\n${v.due.map(x => `${x.stop}: ${dueText(x.inSec)}`).join('\n')}\nBoarding opens with the nations update.`;
      }
      table.replaceChildren(...vs.map(v => {
        const row = document.createElement('div'); const b = document.createElement('b'); b.textContent = `${v.kind === 'ship' ? '⛵' : '🎈'} ${v.name}`;
        const sm = document.createElement('small'); sm.textContent = v.docked ? `At ${v.docked} now · next ${v.heading}` : `To ${v.heading} ${dueText(v.due.find(x => x.stop === v.heading)?.inSec ?? 0)}`;
        row.append(b, sm); return row;
      }));
    };
    update();
    clearInterval(this.vesselTimer);
    this.vesselTimer = window.setInterval(() => { if (this.world.hidden) { clearInterval(this.vesselTimer); return; } update(); }, 1000);
    return table;
  }
}
